import React, { useState, useEffect } from 'react';
import {
  Plus, Check, X, Clock, Send, ArrowRight, Filter, Search,
  Users, Briefcase, MapPin, Newspaper, Calendar, MessageSquare,
  Lightbulb, Star, Tag, AlertCircle, ChevronRight, Edit2, Trash2
} from 'lucide-react';
import axios from 'axios';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8787/api';

interface Client {
  id: number;
  name: string;
}

interface WorkflowItem {
  id: number;
  client_id: number;
  post_type_id?: number;
  post_type_name: string;
  post_type_category?: string;
  title: string;
  description?: string;
  notes?: string;
  status: string;
  target_date?: string;
  platform?: string;
  priority: 'low' | 'medium' | 'high' | 'urgent';
  am_approved_by?: string;
  am_approved_at?: string;
  client_approved_by?: string;
  client_approved_at?: string;
  created_at: string;
}

interface PostType {
  id: number;
  name: string;
  category: string;
  count: number;
}

const STATUS_CONFIG = {
  draft: { label: 'Draft', color: 'gray', icon: Edit2 },
  am_review: { label: 'AM Review', color: 'yellow', icon: Clock },
  am_approved: { label: 'AM Approved', color: 'blue', icon: Check },
  client_review: { label: 'Client Review', color: 'purple', icon: Send },
  client_approved: { label: 'Client Approved', color: 'green', icon: Check },
  in_production: { label: 'In Production', color: 'indigo', icon: AlertCircle },
  completed: { label: 'Completed', color: 'green', icon: Check },
  rejected: { label: 'Rejected', color: 'red', icon: X },
  on_hold: { label: 'On Hold', color: 'orange', icon: Clock },
};

const PRIORITY_COLORS = {
  low: 'bg-gray-100 text-gray-700',
  medium: 'bg-blue-100 text-blue-700',
  high: 'bg-orange-100 text-orange-700',
  urgent: 'bg-red-100 text-red-700',
};

const CATEGORY_ICONS: Record<string, any> = {
  team_members: Users,
  services: Briefcase,
  service_areas: MapPin,
  news: Newspaper,
  events: Calendar,
  testimonials: MessageSquare,
  tips: Lightbulb,
  custom: Star,
};

export default function WorkflowDashboard() {
  const [clients, setClients] = useState<Client[]>([]);
  const [selectedClientId, setSelectedClientId] = useState<number>(0);
  const [items, setItems] = useState<WorkflowItem[]>([]);
  const [filteredItems, setFilteredItems] = useState<WorkflowItem[]>([]);
  const [postTypes, setPostTypes] = useState<PostType[]>([]);
  const [stats, setStats] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  // Filters
  const [selectedPostType, setSelectedPostType] = useState<string>('all');
  const [selectedStatus, setSelectedStatus] = useState<string>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedItems, setSelectedItems] = useState<Set<number>>(new Set());

  // Modals
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingItem, setEditingItem] = useState<WorkflowItem | null>(null);

  const [formData, setFormData] = useState({
    post_type_name: '',
    title: '',
    description: '',
    target_date: '',
    platform: 'all',
    priority: 'medium' as 'low' | 'medium' | 'high' | 'urgent',
  });

  useEffect(() => {
    loadClients();
  }, []);

  useEffect(() => {
    if (selectedClientId) {
      loadData();
    }
  }, [selectedClientId]);

  useEffect(() => {
    filterItems();
  }, [items, selectedPostType, selectedStatus, searchQuery]);

  const loadClients = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/clients`);
      setClients(response.data);
      if (response.data.length > 0) {
        setSelectedClientId(response.data[0].id);
      }
    } catch (error) {
      console.error('Failed to load clients:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadData = async () => {
    try {
      setLoading(true);
      const [itemsRes, statsRes] = await Promise.all([
        axios.get(`${API_BASE_URL}/workflow`, { params: { client_id: selectedClientId } }),
        axios.get(`${API_BASE_URL}/workflow/stats/${selectedClientId}`)
      ]);

      setItems(itemsRes.data);
      setStats(statsRes.data);

      // Build post type summary
      const typeMap = new Map<string, { name: string; category: string; count: number }>();
      itemsRes.data.forEach((item: WorkflowItem) => {
        const key = item.post_type_name;
        if (typeMap.has(key)) {
          typeMap.get(key)!.count++;
        } else {
          typeMap.set(key, {
            name: item.post_type_name,
            category: item.post_type_category || 'custom',
            count: 1
          });
        }
      });

      setPostTypes(Array.from(typeMap.values()));
    } catch (error) {
      console.error('Failed to load data:', error);
    } finally {
      setLoading(false);
    }
  };

  const filterItems = () => {
    let filtered = [...items];

    if (selectedPostType !== 'all') {
      filtered = filtered.filter(item => item.post_type_name === selectedPostType);
    }

    if (selectedStatus !== 'all') {
      filtered = filtered.filter(item => item.status === selectedStatus);
    }

    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(item =>
        item.title.toLowerCase().includes(query) ||
        item.description?.toLowerCase().includes(query)
      );
    }

    setFilteredItems(filtered);
  };

  const handleStatusChange = async (itemId: number, newStatus: string) => {
    try {
      await axios.post(`${API_BASE_URL}/workflow/${itemId}/status`, {
        status: newStatus,
        performed_by: 'Current User' // TODO: Get from auth
      });
      await loadData();
    } catch (error) {
      console.error('Failed to update status:', error);
      alert('Failed to update status');
    }
  };

  const handleBulkStatusChange = async (newStatus: string) => {
    if (selectedItems.size === 0) {
      alert('Please select items first');
      return;
    }

    try {
      await axios.post(`${API_BASE_URL}/workflow/bulk-status`, {
        item_ids: Array.from(selectedItems),
        status: newStatus,
        performed_by: 'Current User'
      });
      setSelectedItems(new Set());
      await loadData();
    } catch (error) {
      console.error('Failed to bulk update:', error);
      alert('Failed to update items');
    }
  };

  const handleCreate = async () => {
    try {
      if (!formData.title.trim() || !formData.post_type_name) {
        alert('Please fill in required fields');
        return;
      }

      await axios.post(`${API_BASE_URL}/workflow`, {
        ...formData,
        client_id: selectedClientId,
        status: 'draft',
        created_by: 'Current User'
      });

      setShowCreateModal(false);
      resetForm();
      await loadData();
    } catch (error: any) {
      console.error('Failed to create item:', error);
      alert(`Failed to create item: ${error.response?.data?.error || error.message}`);
    }
  };

  const handleDelete = async (id: number) => {
    if (!confirm('Are you sure you want to delete this item?')) return;

    try {
      await axios.delete(`${API_BASE_URL}/workflow/${id}`);
      await loadData();
    } catch (error) {
      console.error('Failed to delete item:', error);
      alert('Failed to delete item');
    }
  };

  const toggleItemSelection = (id: number) => {
    const newSelected = new Set(selectedItems);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedItems(newSelected);
  };

  const resetForm = () => {
    setFormData({
      post_type_name: '',
      title: '',
      description: '',
      target_date: '',
      platform: 'all',
      priority: 'medium',
    });
  };

  const getStatusColor = (status: string) => {
    const config = STATUS_CONFIG[status as keyof typeof STATUS_CONFIG];
    return config ? `bg-${config.color}-100 text-${config.color}-700` : 'bg-gray-100 text-gray-700';
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric'
    });
  };

  if (loading && clients.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">Loading...</div>
      </div>
    );
  }

  return (
    <div className="h-screen flex flex-col bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-6 py-4">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Content Workflow</h1>
            <p className="text-sm text-gray-600 mt-1">Manage content from idea to production</p>
          </div>

          <div className="flex items-center gap-3">
            <select
              value={selectedClientId}
              onChange={(e) => setSelectedClientId(parseInt(e.target.value))}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              {clients.map(client => (
                <option key={client.id} value={client.id}>{client.name}</option>
              ))}
            </select>

            <button
              onClick={() => setShowCreateModal(true)}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 flex items-center gap-2"
            >
              <Plus className="w-4 h-4" />
              New Item
            </button>
          </div>
        </div>

        {/* Stats Bar */}
        {stats && (
          <div className="mt-4 grid grid-cols-5 gap-4">
            {stats.by_status.map((stat: any) => {
              const config = STATUS_CONFIG[stat.status as keyof typeof STATUS_CONFIG];
              return (
                <div key={stat.status} className="bg-gray-50 rounded-lg p-3">
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-medium text-gray-600">{config?.label || stat.status}</span>
                    <span className="text-lg font-bold text-gray-900">{stat.count}</span>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Main Content */}
      <div className="flex-1 flex overflow-hidden">
        {/* Left Sidebar - Post Types */}
        <div className="w-64 bg-white border-r border-gray-200 overflow-y-auto">
          <div className="p-4">
            <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">
              Post Types
            </h3>

            <button
              onClick={() => setSelectedPostType('all')}
              className={`w-full flex items-center justify-between px-3 py-2 rounded-lg mb-1 ${
                selectedPostType === 'all'
                  ? 'bg-blue-50 text-blue-700'
                  : 'text-gray-700 hover:bg-gray-50'
              }`}
            >
              <div className="flex items-center gap-2">
                <Tag className="w-4 h-4" />
                <span className="text-sm font-medium">All Items</span>
              </div>
              <span className="text-xs font-semibold">{items.length}</span>
            </button>

            <div className="mt-4 space-y-1">
              {postTypes.map(type => {
                const Icon = CATEGORY_ICONS[type.category] || Tag;
                return (
                  <button
                    key={type.name}
                    onClick={() => setSelectedPostType(type.name)}
                    className={`w-full flex items-center justify-between px-3 py-2 rounded-lg ${
                      selectedPostType === type.name
                        ? 'bg-blue-50 text-blue-700'
                        : 'text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      <Icon className="w-4 h-4" />
                      <span className="text-sm truncate">{type.name}</span>
                    </div>
                    <span className="text-xs font-semibold">{type.count}</span>
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        {/* Right Content - Items */}
        <div className="flex-1 flex flex-col overflow-hidden">
          {/* Filters & Bulk Actions */}
          <div className="bg-white border-b border-gray-200 px-6 py-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search items..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-9 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                  />
                </div>

                <select
                  value={selectedStatus}
                  onChange={(e) => setSelectedStatus(e.target.value)}
                  className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                >
                  <option value="all">All Statuses</option>
                  {Object.entries(STATUS_CONFIG).map(([key, config]) => (
                    <option key={key} value={key}>{config.label}</option>
                  ))}
                </select>
              </div>

              {selectedItems.size > 0 && (
                <div className="flex items-center gap-2">
                  <span className="text-sm text-gray-600">{selectedItems.size} selected</span>
                  <button
                    onClick={() => handleBulkStatusChange('am_review')}
                    className="px-3 py-1 text-xs font-medium text-white bg-yellow-600 rounded hover:bg-yellow-700"
                  >
                    → AM Review
                  </button>
                  <button
                    onClick={() => handleBulkStatusChange('client_review')}
                    className="px-3 py-1 text-xs font-medium text-white bg-purple-600 rounded hover:bg-purple-700"
                  >
                    → Client Review
                  </button>
                  <button
                    onClick={() => setSelectedItems(new Set())}
                    className="px-3 py-1 text-xs font-medium text-gray-700 bg-gray-100 rounded hover:bg-gray-200"
                  >
                    Clear
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Items List */}
          <div className="flex-1 overflow-y-auto p-6">
            <div className="space-y-3">
              {filteredItems.map(item => {
                const statusConfig = STATUS_CONFIG[item.status as keyof typeof STATUS_CONFIG];
                const StatusIcon = statusConfig?.icon || Clock;
                const isSelected = selectedItems.has(item.id);

                return (
                  <div
                    key={item.id}
                    className={`bg-white border-2 rounded-lg p-4 hover:shadow-md transition ${
                      isSelected ? 'border-blue-500' : 'border-gray-200'
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => toggleItemSelection(item.id)}
                        className="mt-1 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />

                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between mb-2">
                          <div className="flex-1">
                            <h4 className="text-base font-semibold text-gray-900">{item.title}</h4>
                            <div className="flex items-center gap-2 mt-1">
                              <span className="text-xs text-gray-500">{item.post_type_name}</span>
                              <span className="text-xs text-gray-400">•</span>
                              <span className="text-xs text-gray-500">{formatDate(item.target_date)}</span>
                            </div>
                          </div>

                          <div className="flex items-center gap-2">
                            <span className={`px-2 py-1 text-xs font-medium rounded-full ${PRIORITY_COLORS[item.priority]}`}>
                              {item.priority}
                            </span>
                            <button
                              onClick={() => handleDelete(item.id)}
                              className="p-1 text-gray-400 hover:text-red-600"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </div>

                        {item.description && (
                          <p className="text-sm text-gray-600 mb-3">{item.description}</p>
                        )}

                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <span className={`inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded ${getStatusColor(item.status)}`}>
                              <StatusIcon className="w-3 h-3" />
                              {statusConfig?.label || item.status}
                            </span>

                            {item.platform && item.platform !== 'all' && (
                              <span className="text-xs text-gray-500 capitalize">{item.platform}</span>
                            )}
                          </div>

                          <div className="flex items-center gap-1">
                            {item.status === 'draft' && (
                              <button
                                onClick={() => handleStatusChange(item.id, 'am_review')}
                                className="px-3 py-1 text-xs font-medium text-yellow-700 bg-yellow-50 rounded hover:bg-yellow-100"
                              >
                                Submit to AM →
                              </button>
                            )}
                            {item.status === 'am_review' && (
                              <>
                                <button
                                  onClick={() => handleStatusChange(item.id, 'am_approved')}
                                  className="px-3 py-1 text-xs font-medium text-green-700 bg-green-50 rounded hover:bg-green-100"
                                >
                                  Approve
                                </button>
                                <button
                                  onClick={() => handleStatusChange(item.id, 'rejected')}
                                  className="px-3 py-1 text-xs font-medium text-red-700 bg-red-50 rounded hover:bg-red-100"
                                >
                                  Reject
                                </button>
                              </>
                            )}
                            {item.status === 'am_approved' && (
                              <button
                                onClick={() => handleStatusChange(item.id, 'client_review')}
                                className="px-3 py-1 text-xs font-medium text-purple-700 bg-purple-50 rounded hover:bg-purple-100"
                              >
                                Send to Client →
                              </button>
                            )}
                            {item.status === 'client_review' && (
                              <>
                                <button
                                  onClick={() => handleStatusChange(item.id, 'client_approved')}
                                  className="px-3 py-1 text-xs font-medium text-green-700 bg-green-50 rounded hover:bg-green-100"
                                >
                                  Client Approved
                                </button>
                                <button
                                  onClick={() => handleStatusChange(item.id, 'rejected')}
                                  className="px-3 py-1 text-xs font-medium text-red-700 bg-red-50 rounded hover:bg-red-100"
                                >
                                  Rejected
                                </button>
                              </>
                            )}
                            {item.status === 'client_approved' && (
                              <button
                                onClick={() => handleStatusChange(item.id, 'in_production')}
                                className="px-3 py-1 text-xs font-medium text-indigo-700 bg-indigo-50 rounded hover:bg-indigo-100"
                              >
                                Start Production →
                              </button>
                            )}
                            {item.status === 'in_production' && (
                              <button
                                onClick={() => handleStatusChange(item.id, 'completed')}
                                className="px-3 py-1 text-xs font-medium text-green-700 bg-green-50 rounded hover:bg-green-100"
                              >
                                Mark Complete
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}

              {filteredItems.length === 0 && (
                <div className="text-center py-12">
                  <AlertCircle className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                  <p className="text-gray-500">No items found</p>
                  <button
                    onClick={() => setShowCreateModal(true)}
                    className="mt-4 px-4 py-2 text-sm font-medium text-blue-600 hover:text-blue-700"
                  >
                    Create your first item
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Create Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">New Content Item</h3>
              <button
                onClick={() => {
                  setShowCreateModal(false);
                  resetForm();
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Post Type *
                </label>
                <input
                  type="text"
                  value={formData.post_type_name}
                  onChange={(e) => setFormData({...formData, post_type_name: e.target.value})}
                  placeholder="e.g., Team Member Spotlight"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Title *
                </label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData({...formData, title: e.target.value})}
                  placeholder="e.g., Meet Mike - 10 Years of Service"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Description
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  placeholder="Brief description..."
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Target Date
                  </label>
                  <input
                    type="date"
                    value={formData.target_date}
                    onChange={(e) => setFormData({...formData, target_date: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Priority
                  </label>
                  <select
                    value={formData.priority}
                    onChange={(e) => setFormData({...formData, priority: e.target.value as any})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="low">Low</option>
                    <option value="medium">Medium</option>
                    <option value="high">High</option>
                    <option value="urgent">Urgent</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Platform
                </label>
                <select
                  value={formData.platform}
                  onChange={(e) => setFormData({...formData, platform: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="all">All Platforms</option>
                  <option value="facebook">Facebook</option>
                  <option value="instagram">Instagram</option>
                  <option value="linkedin">LinkedIn</option>
                  <option value="twitter">Twitter</option>
                </select>
              </div>
            </div>

            <div className="mt-6 flex items-center justify-end gap-3">
              <button
                onClick={() => {
                  setShowCreateModal(false);
                  resetForm();
                }}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleCreate}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700"
              >
                Create Item
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}